/**
 * canvas-engine/presets/presetData/dynamicPresets.js
 *
 * TRANSCRIBED Dynamic_Data_Section default chrome VALUES + binding markers.
 *
 * ZERO-REUSE CONSTRAINT (Task 3.1): imports NOTHING. The authored chrome
 * (headings, subheadings, empty-state copy, labels, default cards) is
 * transcribed from the Default_Source dynamic shells (education courses/cart,
 * retail products/cart, flexOffice bookings) as plain DATA.
 * No React, no rendering, no side effects.
 *
 * Each descriptor expresses a Dynamic_Data_Section's authored chrome PLUS a
 * `binding` marker `{ source, params }`. The dynamic adapter (Task 3.3
 * `dynamic.js`) decomposes the chrome into editable Canvas_Elements AND emits at
 * least one `dataBound` element carrying the binding, so live list content is
 * sourced at render time rather than hand-authored (Req 25.1, 25.4).
 *
 * NOTE: the default cards below are placeholder chrome only; the live list is
 * sourced through the dataBound element's `binding.source`.
 *
 * Requirements: 3.6, 3.7, 5.1, 5.2, 5.4, 5.5, 5.6, 5.7
 */

const action = (label, url, variant = 'primary') => ({ label, url, variant });
const block = (title, description = '', extra = {}) => ({ title, description, visible: true, ...extra });
const style = (overrides = {}) => ({
  background: '#f8fafc',
  textColor: '#1f2937',
  accentColor: '#3B9394',
  padding: '64px 24px',
  ...overrides,
});

/**
 * DYNAMIC_PRESETS — per-dynamic-type default descriptors.
 * Keys: products, courses, bookings, cart. Each carries a `binding` marker the
 * dynamic adapter turns into a `dataBound` element.
 */
export const DYNAMIC_PRESETS = {
  // Live product listing (retail / generic storefront).
  products: {
    sectionType: 'dynamicProducts',
    label: 'Products (dynamic)',
    domain: 'dynamic',
    style: style({ background: '#f8fafc', accentColor: '#0891b2', padding: '72px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 4 },
    content: {
      heading: 'All Products',
      subheading: 'Browse the live product catalog with filters and sorting.',
      searchPlaceholder: 'Search products...',
      sortLabel: 'Sort by',
      emptyStateTitle: 'Products will appear here once published.',
      buttonLabel: 'Add to Cart',
    },
    binding: { source: 'products', params: {} },
  },

  // Live course listing (education).
  courses: {
    sectionType: 'dynamicCourses',
    label: 'Courses (dynamic)',
    domain: 'dynamic',
    style: style({ background: '#f8fafc', accentColor: '#3B9394', secondaryColor: '#87927c', padding: '56px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 4 },
    content: {
      heading: 'Courses',
      subheading: 'Discover expert-led courses that match your career goals.',
      emptyStateTitle: 'Courses will appear after they are published.',
      buttonLabel: 'View course',
    },
    binding: { source: 'validCourses', params: { route: '/course-detail' } },
  },

  // Live booking listing (flexOffice).
  bookings: {
    sectionType: 'dynamicBookings',
    label: 'Bookings (dynamic)',
    domain: 'dynamic',
    style: style({ background: '#f8fafc', accentColor: '#23196E', secondaryColor: '#0f172a', padding: '96px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 3 },
    content: {
      heading: 'My Bookings',
      subheading: 'Review upcoming and past reservations from your workspace account.',
      emptyStateTitle: 'Bookings will appear here',
      cards: [
        block('Executive Conference Room', 'Tomorrow, 10:00 AM - 12:00 PM', { badge: 'Confirmed', price: '$90' }),
        block('Dedicated Workstation', 'Friday, 09:00 AM - 05:00 PM', { badge: 'Upcoming', price: '$72' }),
      ],
    },
    binding: { source: 'bookings', params: {} },
  },

  // Live cart summary (shared across domains).
  cart: {
    sectionType: 'dynamicCart',
    label: 'Cart (dynamic)',
    domain: 'dynamic',
    style: style({ background: '#f8fafc', accentColor: '#0891b2', secondaryColor: '#232f3e', padding: '72px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      heading: 'Shopping Cart',
      subheading: 'Review your items before checkout.',
      summaryTitle: 'Order Summary',
      subtotalLabel: 'Subtotal',
      taxLabel: 'Estimated tax',
      totalLabel: 'Total',
      checkoutLabel: 'Proceed to Checkout',
      emptyStateTitle: 'Your cart is empty.',
      actions: [action('Proceed to Checkout', '/checkout', 'primary')],
    },
    binding: { source: 'cart', params: {} },
  },
};

export default DYNAMIC_PRESETS;
